import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../components/Sidebar';
import { api, type Subscription, type SubscriptionConfig } from '../api/client';
import { T } from '../styles/tokens';

export default function Dashboard() {
  const nav = useNavigate();
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.subs
      .list()
      .then(setSubs)
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const active = subs.filter((s) => s.status === 'active');
  const minDays = active.reduce((m, s) => (m === -1 ? s.days_left : Math.min(m, s.days_left)), -1);

  return (
    <div style={d.root}>
      <Sidebar active="dashboard" />
      <main style={d.main}>
        <div style={d.topBar}>
          <div>
            <div style={d.pageTag}>// личный кабинет</div>
            <div style={d.pageTitle}>Мои подписки</div>
          </div>
          <button style={d.ctaBtn} onClick={() => nav('/purchase')}>
            + Новая подписка
          </button>
        </div>

        <div style={d.statsStrip}>
          {[
            { val: String(active.length), label: 'Активных', accent: T.success },
            { val: String(subs.length), label: 'Устройств', accent: T.accent },
            { val: minDays >= 0 ? String(minDays) : '—', label: 'Дней осталось', accent: T.warn },
            { val: '—', label: 'Потрачено', accent: T.textSec },
          ].map((st) => (
            <div key={st.label} style={d.statItem}>
              <div style={{ ...d.statVal, color: st.accent }}>{st.val}</div>
              <div style={d.statLabel}>{st.label}</div>
            </div>
          ))}
        </div>

        <div style={d.subsLabel}>// конфигурации</div>
        {loading && <div style={{ color: T.textMuted, padding: 20 }}>Загрузка...</div>}
        {!loading && subs.length === 0 && (
          <div style={{ ...d.nudge, marginTop: 0 }}>
            <div>
              <div style={d.nudgeTitle}>У вас ещё нет подписок</div>
              <div style={d.nudgeSub}>Начните с бесплатного 7-дневного триала</div>
            </div>
            <button style={d.ctaBtn} onClick={() => nav('/purchase')}>Выбрать тариф →</button>
          </div>
        )}
        {subs.map((sub) => (
          <SubCard key={sub.id} sub={sub} onBuy={() => nav('/purchase')} />
        ))}

        <div style={d.nudge}>
          <div>
            <div style={d.nudgeTitle}>Пригласите друга — вы оба получите +7 дней</div>
            <div style={d.nudgeSub}>Реферальная программа · автоматически</div>
          </div>
          <button style={d.outlineBtn} onClick={() => nav('/referral')}>
            Получить ссылку →
          </button>
        </div>
      </main>
    </div>
  );
}

function SubCard({ sub, onBuy }: { sub: Subscription; onBuy: () => void }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<SubscriptionConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isActive = sub.status === 'active';
  const isWarn = sub.days_left > 0 && sub.days_left <= 7;
  const accentColor = sub.protocol === 'wireguard' ? T.accent : T.success;
  const pct = sub.total_days ? Math.min(100, (sub.days_left / sub.total_days) * 100) : 0;

  const ensureConfig = async () => {
    if (cfg) return cfg;
    setLoading(true);
    setError(null);
    try {
      const c = await api.subs.getConfig(sub.id);
      setCfg(c);
      return c;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки конфига');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !cfg) await ensureConfig();
  };

  const handleCopy = async () => {
    const c = cfg ?? (await ensureConfig());
    if (!c) return;
    try {
      await navigator.clipboard.writeText(c.link || c.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      setError('Не удалось скопировать');
    }
  };

  const handleDownload = async () => {
    try {
      await api.subs.download(sub.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось скачать');
    }
  };

  const handleReissue = async () => {
    if (!confirm('Перевыпустить конфигурацию? Старый ключ перестанет работать.')) return;
    setReissuing(true);
    setError(null);
    try {
      const c = await api.subs.reissue(sub.id);
      setCfg(c);
      setOpen(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось перевыпустить');
    } finally {
      setReissuing(false);
    }
  };

  return (
    <div style={{ ...d.subCard, borderLeft: `3px solid ${isActive ? accentColor : T.border}`, opacity: isActive ? 1 : 0.55 }}>
      <div style={d.subTop}>
        <div style={d.subLeft}>
          <div style={d.subProtoTag}>{sub.protocol === 'wireguard' ? 'WireGuard' : 'VLESS Reality'}</div>
          <div style={d.subDevice}>{sub.device_label || 'Подписка #' + sub.id}</div>
          <div style={d.subMeta}>
            <span>Тариф: {sub.plan_label || '—'}</span>
            <span style={d.metaDot} />
            <span>До {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString('ru') : '—'}</span>
            <span style={d.metaDot} />
            <span style={{ color: isActive ? (isWarn ? T.warn : T.success) : T.textMuted }}>
              {isActive ? (isWarn ? `⚠ ${sub.days_left} дн.` : `${sub.days_left} дн. осталось`) : 'Истекла'}
            </span>
          </div>
        </div>
        <div style={d.subActions}>
          {isActive && (
            <button style={d.ghostBtn} onClick={toggle} disabled={loading}>
              {loading ? '...' : open ? 'Скрыть' : 'Конфиг'}
            </button>
          )}
          {isActive && (
            <button style={d.ghostBtn} onClick={onBuy}>
              Продлить
            </button>
          )}
          {!isActive && (
            <button style={d.ctaSmBtn} onClick={onBuy}>
              Продлить
            </button>
          )}
        </div>
      </div>
      {isActive && (
        <div style={d.progress}>
          <div style={{ ...d.progressFill, width: `${pct}%`, background: isWarn ? T.warn : accentColor }} />
        </div>
      )}
      {open && (
        <div style={d.configBox}>
          {loading && !cfg && <div style={d.configLabel}>Загрузка конфигурации…</div>}
          {error && <div style={d.configError}>⚠ {error}</div>}
          {cfg && (
            <div style={d.configGrid}>
              <img
                src={`data:image/png;base64,${cfg.qr_png_base64}`}
                alt="QR"
                style={d.qrImg}
              />
              <div style={d.configRight}>
                <div style={d.configTop}>
                  <span style={d.configLabel}>{cfg.filename}</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={d.copyBtn} onClick={handleCopy}>
                      {copied ? '✓ Скопировано' : 'Копировать'}
                    </button>
                    <button style={d.copyBtn} onClick={handleDownload}>
                      Скачать
                    </button>
                    <button style={d.copyBtn} onClick={handleReissue} disabled={reissuing}>
                      {reissuing ? '...' : 'Перевыпустить'}
                    </button>
                  </div>
                </div>
                <code style={d.configCode}>{cfg.content}</code>
                {cfg.note === 'regenerated' && (
                  <div style={d.configHint}>✓ Конфигурация перевыпущена — старая более недействительна.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const d: Record<string, CSSProperties> = {
  root: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', color: T.text },
  main: { flex: 1, marginLeft: 220, padding: '36px 44px', boxSizing: 'border-box', maxWidth: 'calc(100vw - 220px)' },
  topBar: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, gap: 16 },
  pageTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 },
  pageTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, letterSpacing: '-0.03em', color: T.text },
  ctaBtn: { background: T.accent, border: 'none', borderRadius: 4, padding: '11px 22px', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  ctaSmBtn: { background: T.accent, border: 'none', borderRadius: 3, padding: '7px 14px', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  outlineBtn: { background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 18px', color: T.text, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  ghostBtn: { background: T.surfaceUp, border: `1px solid ${T.border}`, borderRadius: 3, padding: '7px 14px', color: T.textSec, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  statsStrip: { display: 'flex', gap: 0, marginBottom: 36, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` },
  statItem: { flex: 1, padding: '20px 0 20px 24px', borderRight: `1px solid ${T.border}` },
  statVal: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, letterSpacing: '-0.03em', marginBottom: 4 },
  statLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' },
  subsLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 12 },
  subCard: { background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.accent}`, padding: '20px 24px', marginBottom: 8, borderRadius: 4 },
  subTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 },
  subLeft: {},
  subProtoTag: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.accent, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 },
  subDevice: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17, letterSpacing: '-0.02em', marginBottom: 6, color: T.text },
  subMeta: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textSec, fontWeight: 500, flexWrap: 'wrap' },
  metaDot: { width: 3, height: 3, borderRadius: '50%', background: T.textMuted, display: 'inline-block', flexShrink: 0 },
  subActions: { display: 'flex', gap: 8, flexShrink: 0 },
  progress: { height: 2, background: T.border, borderRadius: 1, marginTop: 16, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 1, transition: 'width 0.3s' },
  configBox: { marginTop: 16, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 3, padding: '14px 16px' },
  configTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  configLabel: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' },
  copyBtn: { background: 'none', border: `1px solid ${T.border}`, borderRadius: 2, padding: '3px 10px', color: T.textMuted, fontSize: 10, cursor: 'pointer', fontFamily: 'DM Mono, monospace', letterSpacing: '0.05em' },
  configCode: { display: 'block', fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'oklch(0.75 0.15 30)', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-all' as const, maxHeight: 240, overflowY: 'auto' as const },
  configGrid: { display: 'grid', gridTemplateColumns: '140px 1fr', gap: 16, alignItems: 'start' },
  configRight: { minWidth: 0 },
  configError: { padding: '10px 12px', border: '1px solid oklch(0.58 0.19 27 / 0.5)', background: 'oklch(0.58 0.19 27 / 0.08)', color: 'oklch(0.72 0.19 27)', fontFamily: 'DM Mono, monospace', fontSize: 11, borderRadius: 3, marginBottom: 10 },
  configHint: { marginTop: 10, fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.success, letterSpacing: '0.03em' },
  qrImg: { width: 140, height: 140, background: '#fff', padding: 8, borderRadius: 4, border: `1px solid ${T.border}`, imageRendering: 'pixelated' as const },
  nudge: { marginTop: 20, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' },
  nudgeTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, letterSpacing: '-0.01em', marginBottom: 4 },
  nudgeSub: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.06em' },
};
